/**
 * Read-only version of DealAnalysis for public share links
 * KOPS-416: Used in PublicDealReport component
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Nav, Tab, Badge, Spinner, Alert, Table } from 'react-bootstrap';
import { 
  FaFire, FaChartLine, FaDollarSign, FaClock, 
  FaExclamationTriangle, FaMapMarkedAlt, FaCamera, FaHistory
} from 'react-icons/fa';
import dealService, { DealDetail, MLSData } from '../../services/dealService';
import DealMap from './DealMap';
import DealFinancials from './DealFinancials';
import DealCompsReadOnly from './DealCompsReadOnly';
import DealMLSDetails from './DealMLSDetails';

interface DealAnalysisReadOnlyProps {
  deal: DealDetail;
  compDetails?: any[];
  mlsPhotos?: string[]; // Pre-fetched photos from backend
  compPhotos?: any; // Pre-fetched comp photos and details
  listingAgent?: {
    name: string;
    officeName: string;
  };
  propertyDetails?: {
    propertyType: string;
    daysOnMarket: number;
    lotSize: number;
  };
}

const DealAnalysisReadOnly: React.FC<DealAnalysisReadOnlyProps> = ({ deal, compDetails = [], mlsPhotos = [], compPhotos = {}, listingAgent, propertyDetails }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mlsData, setMlsData] = useState<MLSData | null>(null);

  useEffect(() => {
    // For public reports, MLS data should be pre-loaded from backend
    // We'll construct the MLSData object from the provided photos
    console.log('[DealAnalysisReadOnly] useEffect triggered, mlsPhotos:', mlsPhotos?.length);
    if (mlsPhotos && mlsPhotos.length > 0) {
      console.log('[DealAnalysisReadOnly] Setting MLS data with photos');
      setMlsData({
        listing_id: deal.listing_id,
        unparsed_address: deal.unparsed_address || deal.address,
        list_price: deal.list_price,
        original_list_price: deal.list_price,
        living_area: deal.living_area,
        bedrooms: deal.bedrooms,
        bathrooms: deal.bathrooms,
        year_built: deal.year_built,
        city: deal.city,
        state: 'AZ',
        postal_code: deal.postal_code,
        images: mlsPhotos,
        photos: mlsPhotos,
        public_remarks: '',
        listing_agent: null,
        virtual_tour_url: null,
        tax_amount: null,
        hoa_fee: null,
        elementary_school: null,
        middle_school: null,
        high_school: null
      } as unknown as MLSData);
    } else {
      console.log('[DealAnalysisReadOnly] No mlsPhotos provided');
    }
  }, [mlsPhotos, deal]);

  const getGradeStyles = () => {
    const color = dealService.getGradeColor(deal.flip_grade);
    return {
      backgroundColor: color,
      color: 'white'
    };
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="mb-0">
              {deal.unparsed_address || deal.address || `${deal.city}, AZ ${deal.postal_code}`}
            </h3>
          </div>
        </Col>
      </Row>

      {/* Key Metrics Cards */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                style={{
                  width: '60px',
                  height: '60px',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  ...getGradeStyles()
                }}
              >
                {deal.flip_grade}
              </div>
              <h6 className="text-muted small mb-1">Flip Grade</h6>
              <small className="text-muted">{(deal as any).grade_reason || ''}</small>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <FaDollarSign className="text-success mb-2" size={24} />
              <h6 className="text-muted small mb-1">List Price</h6>
              <h4 className="mb-0">{dealService.formatCurrency(Number(deal.list_price || 0))}</h4>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <FaChartLine className="text-primary mb-2" size={24} />
              <h6 className="text-muted small mb-1">ROI</h6>
              <h4 className="mb-0">{Number(deal.flip_roi || 0).toFixed(1)}%</h4>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <FaFire className="text-info mb-2" size={24} />
              <h6 className="text-muted small mb-1">Profit</h6>
              <h4 className="mb-0">{dealService.formatCurrency(Number(deal.flip_profit || 0))}</h4>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <FaClock className="text-warning mb-2" size={24} />
              <h6 className="text-muted small mb-1">Days on Market</h6>
              <h4 className="mb-0">{propertyDetails?.daysOnMarket || deal.days_on_market || 'N/A'}</h4>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <FaExclamationTriangle className="text-danger mb-2" size={24} />
              <h6 className="text-muted small mb-1">Distress Score</h6>
              <h4 className="mb-0">{deal.distress_score}/5</h4>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')}>
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="overview">
              <FaCamera className="me-2" />
              Overview
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="financials">
              <FaDollarSign className="me-2" />
              Financials
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="comps">
              <FaHistory className="me-2" />
              Comps ({deal.total_comps})
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="map">
              <FaMapMarkedAlt className="me-2" />
              Map
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="overview">
            <Row className="g-4">
              <Col lg={8}>
                <DealMLSDetails deal={{ ...deal, ...mlsData }} mlsImages={mlsPhotos} listingAgent={listingAgent} propertyDetails={propertyDetails} />
              </Col>
              <Col lg={4}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Property Details</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td className="text-muted">MLS #</td>
                          <td className="text-end">{deal.listing_id || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Property Type</td>
                          <td className="text-end">{propertyDetails?.propertyType || deal.property_subtype || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Year Built</td>
                          <td className="text-end">{deal.year_built || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Living Area</td>
                          <td className="text-end">{Number(deal.living_area || 0).toLocaleString()} sq ft</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Lot Size</td>
                          <td className="text-end">{propertyDetails?.lotSize ? `${propertyDetails.lotSize.toLocaleString()} sq ft` : Number(deal.lot_size_sqft || 0).toLocaleString() + ' sq ft'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Bedrooms</td>
                          <td className="text-end">{deal.bedrooms || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Bathrooms</td>
                          <td className="text-end">{deal.bathrooms || 'N/A'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>

                <Card className="mt-3">
                  <Card.Header>
                    <h5 className="mb-0">Investment Analysis</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td className="text-muted">ARV (After Repair Value)</td>
                          <td className="text-end fw-bold">{dealService.formatCurrency(Number(deal.renovated_arv || 0))}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Estimated Rehab Cost</td>
                          <td className="text-end">{dealService.formatCurrency(Number(deal.reno_estimate || 0))}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">MAO (Max Allowable Offer)</td>
                          <td className="text-end">{dealService.formatCurrency(Number(deal.mao_20pct || 0))}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">MAO Gap %</td>
                          <td className="text-end">
                            <Badge bg={deal.mao_gap_pct > 0 ? 'success' : 'danger'}>
                              {Number(deal.mao_gap_pct || 0).toFixed(1)}%
                            </Badge>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
          
          <Tab.Pane eventKey="financials">
            <DealFinancials deal={deal} />
          </Tab.Pane>
          
          <Tab.Pane eventKey="comps">
            <DealCompsReadOnly deal={deal} compDetails={compDetails} compPhotos={compPhotos} />
          </Tab.Pane>
          
          <Tab.Pane eventKey="map">
            <DealMap deal={deal} compDetails={compDetails} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

export default DealAnalysisReadOnly;